import { getDesignExpressionVariables } from '@shared/helpers/design/expressions/design-expression-variables.helpers';
import { sumStoreysFromDesignExpressionVariables } from '@shared/helpers/design/expressions/storey-sum-from-design.helpers';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import { emptyStoreySummedValues } from '@shared/helpers/storeys.helpers';
import type { BoverketBtaFields } from './boverket-climate-declaration-bta.helpers';
import {
  buildBoverketClimateImpactFieldsFromVersion,
  buildBoverketVersionDerivedBuildingFields,
  countNonMezzanineStoreys,
  getGarageAreaFromDesign,
  type BoverketClimateImpactFields,
  type BoverketVersionExportContext,
} from './boverket-climate-declaration-version.helpers';

export type BoverketBuildingFields = BoverketBtaFields & {
  vaningsplanOvanMark: number;
  antalKallarPlan: number;
  garageArea: number;
  solcellerArea: number;
  balkongBTA: number;
  balkongOPA: number;
  loftgangarBTA: number;
  loftgangarOPA: number;
  barandekonstruktionGrundlaggningBetong: number;
  barandekonstruktionGrundlaggningStal: number;
  barandekonstruktionGrundlaggningTra: number;
  barandekonstruktionGrundlaggningOvrigt1Procent: number;
  barandekonstruktionGrundlaggningOvrigt2Procent: number;
  barandekonstruktionGrundlaggningOvrigt1Konstruktionsdel: string;
  barandekonstruktionGrundlaggningOvrigt2Konstruktionsdel: string;
  barandekonstruktionOvrigaBetong: number;
  barandekonstruktionOvrigaStal: number;
  barandekonstruktionOvrigaTra: number;
  barandekonstruktionOvrigaOvrigt1Procent: number;
  barandekonstruktionOvrigaOvrigt1Konstruktionsdel: string;
  barandekonstruktionOvrigaOvrigt2Procent: number;
  barandekonstruktionOvrigaOvrigt2Konstruktionsdel: string;
  energiklass: string;
};

export type BoverketClimateFields = BoverketClimateImpactFields;

/**
 * Count non-mezzanine storeys with GFA above ground.
 */
export const countStoreysAboveGroundFromDesign = (
  design: BuildingDesign,
): number =>
  countNonMezzanineStoreys(design, (storey) => storey.gfa_above_ground > 0);

/**
 * Count non-mezzanine storeys with GFA below ground (basement).
 */
export const countBasementStoreysFromDesign = (
  design: BuildingDesign,
): number =>
  countNonMezzanineStoreys(design, (storey) => storey.gfa_below_ground > 0);

/**
 * Derive {@link BoverketBuildingFields} from design and optional version results.
 */
export const buildBoverketBuildingFieldsFromDesign = (
  design: BuildingDesign,
  bta: BoverketBtaFields,
  energiklass: string = '',
  versionContext: BoverketVersionExportContext = { design },
): BoverketBuildingFields => {
  const designVariables = getDesignExpressionVariables(design);
  const { sum } = designVariables
    ? sumStoreysFromDesignExpressionVariables(designVariables)
    : { sum: { ...emptyStoreySummedValues } };

  const versionFields =
    buildBoverketVersionDerivedBuildingFields(versionContext);

  return {
    ...bta,
    vaningsplanOvanMark: countStoreysAboveGroundFromDesign(design),
    antalKallarPlan: countBasementStoreysFromDesign(design),
    garageArea: getGarageAreaFromDesign(design),
    solcellerArea: versionFields.solcellerArea,
    balkongBTA: Math.round(sum.gfa_balconies ?? 0),
    balkongOPA: versionFields.balkongOPA,
    loftgangarBTA: versionFields.loftgangarBTA,
    loftgangarOPA: versionFields.loftgangarOPA,
    barandekonstruktionGrundlaggningBetong:
      versionFields.barandekonstruktionGrundlaggningBetong,
    barandekonstruktionGrundlaggningStal:
      versionFields.barandekonstruktionGrundlaggningStal,
    barandekonstruktionGrundlaggningTra:
      versionFields.barandekonstruktionGrundlaggningTra,
    barandekonstruktionGrundlaggningOvrigt1Procent:
      versionFields.barandekonstruktionGrundlaggningOvrigt1Procent,
    barandekonstruktionGrundlaggningOvrigt1Konstruktionsdel:
      versionFields.barandekonstruktionGrundlaggningOvrigt1Konstruktionsdel,
    barandekonstruktionGrundlaggningOvrigt2Procent:
      versionFields.barandekonstruktionGrundlaggningOvrigt2Procent,
    barandekonstruktionGrundlaggningOvrigt2Konstruktionsdel:
      versionFields.barandekonstruktionGrundlaggningOvrigt2Konstruktionsdel,
    barandekonstruktionOvrigaBetong:
      versionFields.barandekonstruktionOvrigaBetong,
    barandekonstruktionOvrigaStal: versionFields.barandekonstruktionOvrigaStal,
    barandekonstruktionOvrigaTra: versionFields.barandekonstruktionOvrigaTra,
    barandekonstruktionOvrigaOvrigt1Procent:
      versionFields.barandekonstruktionOvrigaOvrigt1Procent,
    barandekonstruktionOvrigaOvrigt1Konstruktionsdel:
      versionFields.barandekonstruktionOvrigaOvrigt1Konstruktionsdel,
    barandekonstruktionOvrigaOvrigt2Procent:
      versionFields.barandekonstruktionOvrigaOvrigt2Procent,
    barandekonstruktionOvrigaOvrigt2Konstruktionsdel:
      versionFields.barandekonstruktionOvrigaOvrigt2Konstruktionsdel,
    energiklass,
  };
};

/** Climate impact from version pipeline results. */
export const buildBoverketClimateFieldsFromDesign = (
  context: BoverketVersionExportContext,
): BoverketClimateFields =>
  buildBoverketClimateImpactFieldsFromVersion(context);
